import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  HeartIcon,
  EllipsisVerticalIcon,
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  ChartBarIcon,
  ShareIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid'
import FolderBreadcrumb from './FolderBreadcrumb'
import { BookmarkedUrl } from '@/types/bookmark'
import toast from 'react-hot-toast'
import { bookmarkApi } from '../../../shared/services/bookmarkApi'
import { useTranslation } from 'react-i18next'

interface UrlListItemProps {
  url: BookmarkedUrl
  onUpdate: (url: BookmarkedUrl) => void
  onDelete: (urlId: string) => void
  onCopySuccess: (text: string) => void
  onTagClick?: (tagId: string) => void
}

// 리스트 형태의 URL 아이템 컴포넌트
const UrlListItem: React.FC<UrlListItemProps> = ({
  url,
  onUpdate,
  onDelete,
  onCopySuccess,
  onTagClick,
}) => {
  const { t } = useTranslation()
  const [showActions, setShowActions] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingTitle, setEditingTitle] = useState(url.title || '')

  // 폴더 클릭 핸들러
  const handleFolderClick = (folderId: string) => {
    const event = new CustomEvent('folderNavigate', {
      detail: { folderId },
    })
    window.dispatchEvent(event)
  }

  // 즐겨찾기 토글
  const handleToggleFavorite = async () => {
    try {
      const isFavorite = await bookmarkApi.toggleFavorite(url._id)
      onUpdate({ ...url, isFavorite })
    } catch (error) {
      toast.error(t('link.favoriteError'))
      console.error('Failed to toggle favorite:', error)
    }
  }

  // 제목 수정
  const handleSaveTitle = async () => {
    try {
      await bookmarkApi.update(url._id, { title: editingTitle })
      onUpdate({ ...url, title: editingTitle })
      setIsEditing(false)
    } catch (error) {
      toast.error(t('link.titleUpdateError'))
      console.error('Failed to update title:', error)
    }
  }

  // 링크 열기
  const handleOpenLink = () => {
    window.open(url.originalUrl, '_blank', 'noopener,noreferrer')
  }

  // 복사
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url.shortUrl)
      onCopySuccess(url.shortUrl)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  // 네이티브 공유
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: url.title || 'Shortened URL',
          text: `Check out this link: ${url.title || 'Shortened URL'}`,
          url: url.shortUrl,
        })
      } catch (err) {
        console.error('Error sharing:', err)
        // Fallback to copy
        handleCopy()
      }
    } else {
      // Fallback to copy
      handleCopy()
    }
  }

  // 상대적 시간 계산
  const getRelativeTime = (date: string) => {
    const now = new Date()
    const past = new Date(date)
    const diffMs = now.getTime() - past.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return t('link.today')
    }
    if (diffDays === 1) {
      return t('link.yesterday')
    }
    if (diffDays < 30) {
      return t('link.daysAgo', { count: String(diffDays) })
    }
    if (diffDays < 365) {
      return t('link.monthsAgo', {
        count: String(Math.floor(diffDays / 30)),
      })
    }
    return t('link.yearsAgo', {
      count: String(Math.floor(diffDays / 365)),
    })
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="group rounded-lg border border-gray-200 bg-white transition-all duration-200 hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="p-4">
        {/* 모바일과 데스크톱 분기 처리 */}
        <div className="md:flex md:items-center md:justify-between">
          {/* 상단: 파비콘, 제목, URL (모바일에서는 전체 폭 사용) */}
          <div className="mb-3 flex min-w-0 flex-1 items-start space-x-3 md:mb-0">
            {/* 파비콘 */}
            <div className="mt-1 flex-shrink-0">
              {url.favicon ? (
                <img
                  src={url.favicon}
                  alt=""
                  className="h-6 w-6 rounded"
                  onError={e => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              ) : (
                <div className="flex h-6 w-6 items-center justify-center rounded bg-gray-200 dark:bg-gray-600">
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-400" />
                </div>
              )}
            </div>

            {/* 제목과 URL */}
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editingTitle}
                    onChange={e => setEditingTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSaveTitle()
                      }
                      if (e.key === 'Escape') {
                        setIsEditing(false)
                        setEditingTitle(url.title || '')
                      }
                    }}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveTitle}
                    className="rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                  >
                    {t('link.save')}
                  </button>
                </div>
              ) : (
                <div>
                  <h3 className="text-sm font-medium leading-snug text-gray-900 dark:text-white md:text-base">
                    {url.title || url.originalUrl}
                  </h3>
                  <div className="mt-1 space-y-1">
                    <p className="font-mono text-xs text-blue-600 dark:text-blue-400">
                      {url.shortUrl}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      <span className="inline md:hidden">→ </span>
                      {url.originalUrl}
                    </p>
                    {/* 폴더 정보 추가 */}
                    {url.folderId && (
                      <div className="mt-2">
                        <FolderBreadcrumb
                          folder={url.folderId}
                          onFolderClick={handleFolderClick}
                          className="text-gray-500 dark:text-gray-400"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 데스크톱용 태그 영역 */}
          <div className="mx-4 hidden flex-shrink-0 items-center space-x-2 md:flex">
            {url.tags.length > 0 && (
              <div className="flex max-w-xs flex-wrap gap-1">
                {url.tags.slice(0, 3).map(tag => (
                  <button
                    key={tag._id}
                    onClick={() => onTagClick?.(tag._id)}
                    className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium transition-opacity hover:opacity-80"
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                    }}
                    title={t('tag.filterBy', { name: tag.name })}
                  >
                    {tag.name}
                  </button>
                ))}
                {url.tags.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{url.tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* 데스크톱용 우측 영역: 통계, 액션 */}
          <div className="hidden flex-shrink-0 items-center space-x-4 md:flex">
            {/* 통계 정보 */}
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              {/* 클릭 수 */}
              <div className="flex items-center space-x-1">
                <ChartBarIcon className="h-4 w-4" />
                <span>{url.clickCount}</span>
              </div>

              {/* 생성일 */}
              <div className="flex items-center space-x-1">
                <CalendarIcon className="h-4 w-4" />
                <span>{getRelativeTime(url.createdAt)}</span>
              </div>
            </div>

            {/* 즐겨찾기 버튼 */}
            <button
              onClick={handleToggleFavorite}
              className="rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {url.isFavorite ? (
                <HeartSolidIcon className="h-5 w-5 text-red-500" />
              ) : (
                <HeartIcon className="h-5 w-5 text-gray-400 hover:text-red-500" />
              )}
            </button>

            {/* 액션 메뉴 */}
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <EllipsisVerticalIcon className="h-5 w-5 text-gray-400" />
              </button>

              {/* 액션 드롭다운 */}
              {showActions && (
                <div className="absolute right-0 top-8 z-10 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleOpenLink()
                        setShowActions(false)
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      <ArrowTopRightOnSquareIcon className="mr-3 h-4 w-4" />
                      {t('link.open')}
                    </button>

                    <button
                      onClick={() => {
                        handleCopy()
                        setShowActions(false)
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      <ClipboardIcon className="mr-3 h-4 w-4" />
                      {t('link.copyLink')}
                    </button>

                    <button
                      onClick={() => {
                        handleNativeShare()
                        setShowActions(false)
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      <ShareIcon className="mr-3 h-4 w-4" />
                      {t('link.shareLink')}
                    </button>

                    <button
                      onClick={() => {
                        setIsEditing(true)
                        setShowActions(false)
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      <PencilIcon className="mr-3 h-4 w-4" />
                      {t('link.editTitle')}
                    </button>

                    <div className="border-t border-gray-100 dark:border-gray-600">
                      <button
                        onClick={() => {
                          onDelete(url._id)
                          setShowActions(false)
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="mr-3 h-4 w-4" />
                        {t('link.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 모바일용 하단 영역 */}
        <div className="md:hidden">
          {/* 모바일용 태그들 */}
          {url.tags.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {url.tags.slice(0, 4).map(tag => (
                <button
                  key={tag._id}
                  onClick={() => onTagClick?.(tag._id)}
                  className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium transition-opacity hover:opacity-80 active:scale-95"
                  style={{
                    backgroundColor: `${tag.color}20`,
                    color: tag.color,
                  }}
                  title={t('tag.filterBy', { name: tag.name })}
                >
                  #{tag.name}
                </button>
              ))}
              {url.tags.length > 4 && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                  {t('link.moreTags', { count: String(url.tags.length - 4) })}
                </span>
              )}
            </div>
          )}

          {/* 모바일용 통계 및 액션 */}
          <div className="flex items-center justify-between">
            {/* 통계 정보 */}
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-1">
                <ChartBarIcon className="h-4 w-4" />
                <span className="font-medium">{url.clickCount}</span>
                <span>{t('link.clicks')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <CalendarIcon className="h-4 w-4" />
                <span>{getRelativeTime(url.createdAt)}</span>
              </div>
            </div>

            {/* 모바일 액션 버튼들 */}
            <div className="flex items-center space-x-2">
              {/* 즐겨찾기 버튼 */}
              <button
                onClick={handleToggleFavorite}
                className="rounded-lg p-2.5 transition-colors hover:bg-gray-100 active:scale-95 dark:hover:bg-gray-700"
              >
                {url.isFavorite ? (
                  <HeartSolidIcon className="h-5 w-5 text-red-500" />
                ) : (
                  <HeartIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {/* 공유 버튼 */}
              <button
                onClick={handleNativeShare}
                className="rounded-lg bg-blue-600 p-2.5 text-white transition-colors hover:bg-blue-700 active:scale-95"
              >
                <ShareIcon className="h-4 w-4" />
              </button>

              {/* 더보기 메뉴 */}
              <div className="relative">
                <button
                  onClick={() => setShowActions(!showActions)}
                  className="rounded-lg p-2.5 transition-colors hover:bg-gray-100 active:scale-95 dark:hover:bg-gray-700"
                >
                  <EllipsisVerticalIcon className="h-5 w-5 text-gray-400" />
                </button>

                {/* 모바일 액션 드롭다운 */}
                {showActions && (
                  <div className="absolute bottom-full right-0 z-10 mb-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleOpenLink()
                          setShowActions(false)
                        }}
                        className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        <ArrowTopRightOnSquareIcon className="mr-3 h-5 w-5" />
                        {t('link.open')}
                      </button>

                      <button
                        onClick={() => {
                          handleCopy()
                          setShowActions(false)
                        }}
                        className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        <ClipboardIcon className="mr-3 h-5 w-5" />
                        {t('link.copyLink')}
                      </button>

                      <button
                        onClick={() => {
                          setIsEditing(true)
                          setShowActions(false)
                        }}
                        className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        <PencilIcon className="mr-3 h-5 w-5" />
                        {t('link.editTitle')}
                      </button>

                      <div className="border-t border-gray-100 dark:border-gray-600">
                        <button
                          onClick={() => {
                            onDelete(url._id)
                            setShowActions(false)
                          }}
                          className="flex w-full items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <TrashIcon className="mr-3 h-5 w-5" />
                          {t('link.delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 메모가 있는 경우 */}
        {url.notes && (
          <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm leading-relaxed text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {url.notes}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default UrlListItem
